import { fetchWithAuth } from './http-client';

import type {
  ListDepartmentsParams,
  ListDepartmentMembersParams,
  SaveDepartmentPayload,
  Department,
  DepartmentListResponse,
  DepartmentHierarchyResponse,
  DepartmentMember,
  DepartmentMembersResponse
} from '@/types/departments';

/**
 * Departments API Client
 *
 * @project PROJ-001 - Identity & Organization Management Enhancement
 * @phase Phase 1 - Sprint 1.3 (Frontend UI)
 * @backend Sprint 1.2 - Departments API (1.3.2.1-1.3.2.5)
 */

// Using versioned API endpoints (v1) for proper data transformations
const DEPARTMENT_API_ENV_VAR = 'VITE_DEPARTMENT_API_URL';
const DEPARTMENT_API_BASE_URL = (
  import.meta.env.VITE_DEPARTMENT_API_URL || '/api/v1/departments'
).replace(/\/$/, '');

function buildQuery(params: ListDepartmentsParams = {}): string {
  const searchParams = new URLSearchParams();

  if (params.search) {
    searchParams.set('search', params.search);
  }

  if (params.parentId !== undefined) {
    if (params.parentId === null) {
      searchParams.set('parentId', 'null');
    } else {
      searchParams.set('parentId', params.parentId);
    }
  }

  if (typeof params.includeChildren === 'boolean') {
    searchParams.set('includeChildren', params.includeChildren.toString());
  }

  if (typeof params.page === 'number') {
    searchParams.set('page', params.page.toString());
  }

  if (typeof params.limit === 'number') {
    searchParams.set('limit', params.limit.toString());
  }

  if (params.sortBy) {
    searchParams.set('sortBy', params.sortBy);
  }

  if (params.sortOrder) {
    searchParams.set('sortOrder', params.sortOrder);
  }

  return searchParams.toString();
}

async function send(path: string, init: RequestInit = {}): Promise<Response> {
  const method = init.method ?? 'GET';
  const response = await fetchWithAuth(`${DEPARTMENT_API_BASE_URL}${path}`, {
    ...init,
    method,
    context: `Solicitud de departamentos (${method})`,
    envVar: DEPARTMENT_API_ENV_VAR,
    baseUrl: DEPARTMENT_API_BASE_URL,
  });

  if (!response.ok) {
    let message = response.statusText || `HTTP ${response.status}`;
    try {
      const body = await response.json();
      if (body) {
        if (typeof body.error === 'string') {
          message = body.error;
        } else if (body.error && typeof body.error === 'object') {
          const e = body.error as any;
          message = e.message || e.detail || (e.details && e.details.message) || e.code || message;
        } else if (typeof body.message === 'string') {
          message = body.message;
        }
      }
    } catch {
      // ignore parse error
    }
    throw new Error(message);
  }

  return response;
}

export async function listDepartments(params: ListDepartmentsParams = {}): Promise<DepartmentListResponse> {
  const query = buildQuery(params);
  const response = await send(query ? `?${query}` : '', { method: 'GET' });
  const payload = (await response.json()) as DepartmentListResponse;
  const pagination = (payload.meta as any)?.pagination ?? payload.meta ?? {};
  const limit = Number(pagination.limit ?? params.limit ?? 50);
  const page = Number(pagination.page ?? params.page ?? 1);
  const total = Number(pagination.total ?? (Array.isArray(payload.data) ? payload.data.length : 0));
  const totalPages = Number(
    pagination.totalPages ??
      (limit > 0 ? Math.ceil(total / limit) : total > 0 ? 1 : 0)
  );
  return {
    success: payload.success ?? true,
    data: payload.data ?? [],
    meta: {
      total,
      page,
      limit,
      totalPages,
      hasMore: typeof pagination.hasMore === 'boolean' ? Boolean(pagination.hasMore) : page < totalPages
    }
  };
}

export async function getDepartmentHierarchy(): Promise<DepartmentHierarchyResponse> {
  const response = await send('/hierarchy', { method: 'GET' });
  const payload = (await response.json()) as DepartmentHierarchyResponse;
  return {
    success: payload.success ?? true,
    data: payload.data ?? []
  };
}

export async function getDepartment(id: string): Promise<Department> {
  const response = await send(`/${id}`, { method: 'GET' });
  const payload = (await response.json()) as { data?: Department };
  if (!payload.data) {
    throw new Error('Respuesta inválida del servidor');
  }
  return payload.data;
}

export async function createDepartment(payload: SaveDepartmentPayload): Promise<Department> {
  const response = await send('', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  const body = (await response.json()) as { data?: Department };
  if (!body.data) {
    throw new Error('Respuesta inválida del servidor');
  }
  return body.data;
}

export async function updateDepartment(id: string, payload: Partial<SaveDepartmentPayload>): Promise<Department> {
  const response = await send(`/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload)
  });
  const body = (await response.json()) as { data?: Department };
  if (!body.data) {
    throw new Error('Respuesta inválida del servidor');
  }
  return body.data;
}

export async function deleteDepartment(id: string): Promise<void> {
  await send(`/${id}`, { method: 'DELETE' });
}

export async function checkDepartmentDependencies(id: string): Promise<{ canDelete: boolean; userCount: number; childCount: number; message: string }> {
  const response = await send(`/${id}/dependencies`, { method: 'GET' });
  const body = (await response.json()) as { data?: { canDelete: boolean; userCount: number; childCount: number; message: string } };
  if (!body.data) {
    throw new Error('Respuesta inválida del servidor');
  }
  return body.data;
}

// ============================================
// Department Members API
// ============================================

function buildMembersQuery(params: ListDepartmentMembersParams = {}): string {
  const searchParams = new URLSearchParams();

  if (params.search) {
    searchParams.set('search', params.search);
  }

  if (typeof params.page === 'number') {
    searchParams.set('page', params.page.toString());
  }

  if (typeof params.limit === 'number') {
    searchParams.set('limit', params.limit.toString());
  }

  return searchParams.toString();
}

export async function listDepartmentMembers(
  departmentId: string,
  params: ListDepartmentMembersParams = {}
): Promise<DepartmentMembersResponse> {
  const query = buildMembersQuery(params);
  const response = await send(
    `/${departmentId}/members${query ? `?${query}` : ''}`,
    { method: 'GET' }
  );
  const payload = (await response.json()) as DepartmentMembersResponse;
  const pagination = (payload.meta as any)?.pagination ?? payload.meta ?? {};
  const limit = Number(pagination.limit ?? params.limit ?? 50);
  const page = Number(pagination.page ?? params.page ?? 1);
  const total = Number(pagination.total ?? (Array.isArray(payload.data) ? payload.data.length : 0));
  const totalPages = Number(
    pagination.totalPages ??
      (limit > 0 ? Math.ceil(total / limit) : total > 0 ? 1 : 0)
  );
  return {
    success: payload.success ?? true,
    data: payload.data ?? [],
    meta: {
      total,
      page,
      limit,
      totalPages,
      hasMore: typeof pagination.hasMore === 'boolean' ? Boolean(pagination.hasMore) : page < totalPages
    }
  };
}

export async function addDepartmentMember(
  departmentId: string,
  userId: string
): Promise<void> {
  await send(`/${departmentId}/members`, {
    method: 'POST',
    body: JSON.stringify({ userId })
  });
}

export async function removeDepartmentMember(
  departmentId: string,
  userId: string
): Promise<void> {
  await send(`/${departmentId}/members/${userId}`, {
    method: 'DELETE'
  });
}

// ============================================
// Department Archive / Restore API
// ============================================

export async function archiveDepartment(departmentId: string): Promise<void> {
  await send(`/${departmentId}/archive`, {
    method: 'POST'
  });
}

export async function restoreDepartment(departmentId: string): Promise<void> {
  await send(`/${departmentId}/restore`, {
    method: 'POST'
  });
}
